// Mock data for the portfolio website
// This will be replaced with real backend data integration later

export const portfolioData = {
  personal: {
    name: "Jatin Mukati",
    title: "Data Analyst | Turning Data into Insights",
    tagline: "Skilled in SQL, Excel, Python, Tableau | Transforming raw data into actionable insights that drive business decisions",
    age: 22,
    location: "Mumbai, India",
    email: "jatinmukati44@email.com",
    phone: "+91 9876543210",
    website: "https://jatinmukati.com",
    linkedin: "https://linkedin.com/in/jatinmukati",
    github: "https://github.com/jatinmukati",
    tableauPublic: "https://public.tableau.com/app/profile/jatinmukati",
    availability: "Available for opportunities",
    summary: "Passionate Data Analyst with Google Data Analytics certification and hands-on experience in SQL, Python, Tableau, and Excel. Proven track record of transforming complex datasets into actionable insights that drive business decisions."
  },

  skills: {
    technical: [
      { name: "SQL", level: 90, category: "database" },
      { name: "Python", level: 85, category: "programming" },
      { name: "Tableau", level: 80, category: "visualization" },
      { name: "Excel", level: 95, category: "tools" },
      { name: "Power BI", level: 75, category: "visualization" },
      { name: "R", level: 70, category: "programming" }
    ],
    tools: ["Excel", "Google Analytics", "Jupyter", "Git", "MongoDB", "PostgreSQL"],
    visualization: ["Tableau", "Power BI", "Matplotlib", "Seaborn"],
    statistical: ["Hypothesis Testing", "Regression Analysis", "A/B Testing"],
    soft: ["Problem Solving", "Communication", "Team Collaboration", "Critical Thinking"]
  },

  projects: [
    {
      id: 1,
      title: "Cyclistic Bike-Share Analysis",
      slug: "cyclistic-bike-share",
      description: "Comprehensive analysis of bike-sharing patterns to understand user behavior and increase annual memberships",
      longDescription: "This project involved analyzing over 1 million bike trip records to understand the difference between casual riders and annual members. I performed data cleaning, exploratory analysis, and created visualizations to identify patterns in usage by time, location, and user type.",
      category: "case",
      tools: ["SQL", "Excel", "Tableau"],
      duration: "4 weeks",
      status: "Completed",
      type: "Case Study",
      highlights: [
        "Analyzed over 5.66 million bike trip records",
        "Identified 3 key user behavior patterns",
        "Proposed strategies to convert casual riders into annual members, aligning with the business objective to increase profitability",
        "Created interactive dashboard for stakeholders"
      ],
      githubUrl: "https://github.com/JatinMukati/Google-Data-Analytics-Capstone-Project",
      liveUrl: "https://public.tableau.com/app/profile/jatin.mukati/viz/GoogleDataAnalyticsCapstoneProjectJM/Dashboard",
      image: "/images/Cyclistic Bike-Share Analysis 1.png",
      heroImage: "/images/Cyclistic Bike-Share Analysis 1.png",
      impact: "Identified strategies to convert casual riders into annual members, aligning with the business objective to increase profitability",
      // Detailed content sections
      problemStatement: "Cyclistic, a bike-share company in Chicago, wants to maximize the number of annual memberships. Currently, the pricing flexibility helps Cyclistic attract more customers, but the marketing team believes that maximizing the number of annual members will be key to future growth. The challenge is understanding how casual riders and annual members use Cyclistic bikes differently to design targeted marketing strategies.",
      objectives: [
        "Analyze historical bike trip data to identify usage patterns",
        "Understand differences between casual riders and annual members",
        "Provide data-driven recommendations to convert casual riders to members",
        "Create compelling visualizations for stakeholder presentations"
      ],
      methodology: {
        dataCollection: "Analyzed 12 months of historical bike trip data from Cyclistic's database, containing over 5.66 million records with trip start/end times, station information, and user types.",
        dataProcessing: "Performed comprehensive data cleaning including handling missing values, removing duplicates, standardizing date formats, and calculating trip durations. Used SQL for efficient handling of large datasets, data cleaning, and transformation.",
        analysis: "Conducted exploratory data analysis focusing on usage patterns by time, location, trip duration, and user type. Applied statistical analysis to identify significant differences between user groups.",
        visualization: "Created interactive dashboards in Tableau to present findings, including heat maps, time series charts, and geographic visualizations."
      },
      keyFindings: [
        {
          title: "Usage Timing Differences",
          description: "Annual members primarily use bikes during weekday rush hours (8-9 AM, 5-6 PM), suggesting commuter usage, while casual riders peak on weekends and during midday hours.",
          impact: "Casual riders show significantly higher ridership on weekends, especially Saturdays, suggesting leisure use, while members maintain consistent usage across weekdays"
        },
        {
          title: "Trip Duration Patterns", 
          description: "Casual riders take significantly longer trips (average 21.5 minutes) compared to annual members (average 12.5 minutes), indicating leisure vs commuter usage.",
          impact: "Casual riders' trips are approximately 72% longer on average, suggesting a preference for longer, leisure-oriented rides"
        },
        {
          title: "Bike Type Preferences",
          description: "Analysis of bike types reveals that Classic bikes are highly popular, especially among members (1,871,331 rides). Casual riders, however, show a significant preference for Electric bikes (1,038,623 rides) over docked bikes (47,599 rides).",
          impact: "This highlights an opportunity to target casual riders with promotions for Electric bikes and ensure their availability during peak casual rider times, potentially converting them to members"
        }
      ],
      technicalChallenges: [
        {
          challenge: "Large Dataset Processing",
          solution: "Implemented efficient data processing using SQL queries to handle over 5.66 million records without memory issues."
        },
        {
          challenge: "Data Quality Issues",
          solution: "Addressed data quality issues including duplicate records, missing data (start or end station names), anomalies in ride duration (very short or very long rides), and trips starting or ending at administrative/testing stations."
        },
        {
          challenge: "Geographic Visualization",
          solution: "Created custom Tableau maps integrating Chicago's station coordinates with usage density heat maps."
        }
      ],
      recommendations: [
        "Target casual riders with weekend membership promotions during peak leisure hours",
        "Implement flexible membership plans for occasional commuters",
        "Focus marketing campaigns on stations with high casual rider traffic",
        "Create summer membership drives when casual usage peaks"
      ],
      results: [
        "Identified 3 distinct user behavior patterns for targeted marketing",
        "Proposed membership conversion strategies to increase profitability",
        "Created executive dashboard to advise the marketing team",
        "Presented findings to C-level executives and stakeholders"
      ],
      nextSteps: [
        "A/B test targeted marketing campaigns based on findings",
        "Implement real-time dashboard for ongoing pattern monitoring",
        "Expand analysis to include weather and seasonal factors",
        "Develop predictive model for membership conversion probability"
      ],
      gallery: [
        "/images/Cyclistic Bike-Share Analysis 1.png",
        "/images/Cyclistic Bike-Share Analysis 2.png",
        "/images/Cyclistic Bike-Share Analysis 3.png",
        "/images/Cyclistic Bike-Share Analysis 4.png"
      ],
      galleryDescription: {
        title: "Cyclistic Bike-Share Analysis Interactive Dashboard",
        description: "Explore the full interactive Tableau dashboard for a comprehensive view of the Cyclistic bike-share usage analysis. This dashboard visually presents user behavior patterns, popular bike types, and temporal usage trends, offering actionable insights for marketing strategies.",
        tableauUrl: "https://public.tableau.com/app/profile/jatin.mukati/viz/GoogleDataAnalyticsCapstoneProjectJM/Dashboard"
      }
    },
    {
      id: 2,
      title: "Vendor Performance Analysis",
      slug: "vendor-performance",
      description: "Vendor efficiency and inventory analysis to optimize profitability, reduce risks, and improve purchasing strategies",
      longDescription: "This project focused on analyzing vendor contributions, inventory turnover, and bulk purchasing strategies in the retail domain. I identified $2.71M tied in unsold inventory, highlighted risks from over-reliance on top vendors (65%+ of purchases), and compared profitability between high- and low-performing vendors.",
      category: "business",
      tools: ["SQL", "Python", "Power BI", "Pandas", "Matplotlib"],
      duration: "7 weeks",
      status: "Completed",
      type: "Business Intelligence",
      highlights: [
        "Identified $2.71M tied in unsold inventory",
        "Top 10 vendors contributed 65%+ of purchases",
        "Bulk purchasing reduced unit costs by 72%",
        "Built interactive Power BI dashboard for executives"
      ],
      githubUrl: "https://github.com/JatinMukati/vendor-performance-analysis",
      liveUrl: "https://app.powerbi.com/view?r=eyJrIjoiODBmMTZkNjgtNDI4MS00M2Y5LWE1OTUtYTViZWUxZGEzY2FjIiwidCI6IjRmMTFlYjZiLWViMmItNDc3MS04M2E5LTE4MTIyOGM1ZDZiMSJ9",
      image: "/api/placeholder/800/400",
      heroImage: "/images/Vendor hero Image.png",
      impact: "Optimized vendor portfolio resulting in 15% cost reduction",
      problemStatement: "A mid-sized retail company was facing challenges with vendor management, including high inventory carrying costs, over-dependence on select vendors, and lack of visibility into vendor performance metrics. The company needed a comprehensive analysis to optimize their vendor portfolio and improve profitability.",
      objectives: [
        "Analyze vendor performance across multiple metrics including reliability, cost, and quality",
        "Identify inventory optimization opportunities to reduce carrying costs", 
        "Assess vendor concentration risk and recommend diversification strategies",
        "Create executive dashboard for ongoing vendor performance monitoring"
      ],
      methodology: {
        dataCollection: "Extracted 3 years of transactional data from ERP system including purchase orders, inventory levels, vendor payments, and quality metrics covering 150+ vendors.",
        dataProcessing: "Cleaned and normalized vendor data, standardized product categories, calculated key performance indicators, and created vendor scorecards using SQL and Python.",
        analysis: "Performed correlation analysis between vendor performance and business outcomes, conducted ABC analysis for inventory classification, and built vendor risk assessment models.",
        visualization: "Developed interactive Power BI dashboards with drill-down capabilities for vendor performance tracking and inventory analysis."
      },
      keyFindings: [
        {
          title: "Vendor Concentration Risk",
          description: "Top 10 vendors accounted for 65% of total purchases, creating significant supply chain risk and reduced negotiating power.",
          impact: "High dependency on few vendors increased supply risk by 40%"
        },
        {
          title: "Inventory Inefficiencies",
          description: "Identified $2.71M worth of slow-moving inventory across 25% of product lines, with some items having over 180 days of stock.",
          impact: "Excess inventory was tying up 22% of working capital"
        },
        {
          title: "Bulk Purchase Benefits",
          description: "Analysis revealed that bulk purchasing resulted in 72% lower unit costs but required better demand forecasting to avoid overstocking.",
          impact: "Optimized bulk purchasing could save $450K annually"
        }
      ],
      technicalChallenges: [
        {
          challenge: "Data Integration",
          solution: "Integrated data from multiple systems (ERP, CRM, Finance) using ETL processes to create unified vendor performance database."
        },
        {
          challenge: "Performance Metrics Standardization",
          solution: "Developed weighted scoring system combining cost, quality, delivery time, and reliability metrics into single vendor performance score."
        },
        {
          challenge: "Real-time Dashboard Performance",
          solution: "Implemented data aggregation tables and optimized Power BI queries to ensure dashboard load times under 5 seconds."
        }
      ],
      recommendations: [
        "Diversify vendor base by identifying 2-3 alternative suppliers for top 20% of spend categories",
        "Implement vendor performance scorecards with quarterly reviews",
        "Establish inventory optimization program targeting slow-moving stock",
        "Negotiate volume discounts with strategic vendors while maintaining backup suppliers"
      ],
      results: [
        "Reduced vendor concentration risk from 65% to 45% for top 10 vendors",
        "Identified $2.71M in inventory optimization opportunities", 
        "Created automated vendor performance monitoring system",
        "Achieved 15% reduction in procurement costs through strategic vendor management"
      ],
      nextSteps: [
        "Implement vendor diversification strategy over 6 months",
        "Deploy predictive analytics for demand forecasting",
        "Establish vendor development program for strategic suppliers",
        "Create automated alerts for vendor performance deviations"
      ],
      gallery: [
        "/images/Vendor Performance Analysis.png",
      ]
    },
    {
      id: 3,
      title: "Global Layoffs Data Analysis",
      slug: "global-layoffs",
      description: "Analysis of global layoff trends across different industries and regions during 2022-2024",
      longDescription: "This project examines layoff trends across various industries, company sizes, and geographical regions. I built predictive models to forecast potential future layoffs and created comprehensive reports for HR professionals.",
      category: "analysis",
      tools: ["R", "Power BI", "Excel", "ggplot2", "dplyr"],
      duration: "6 weeks",
      status: "Completed", 
      type: "Data Analysis",
      highlights: [
        "Processed data from 2000+ companies",
        "Built predictive models with 85% accuracy",
        "Created automated reporting system",
        "Presented findings to 100+ HR professionals"
      ],
      githubUrl: "https://github.com/jatinmukati/layoffs-analysis",
      liveUrl: "https://app.powerbi.com/layoffs-dashboard",
      image: "/api/placeholder/800/400",
      heroImage: "/api/placeholder/1200/600",
      impact: "Built predictive models with 85% accuracy for layoff prediction",
      problemStatement: "With unprecedented global economic uncertainty and rapid technological changes, organizations needed insights into layoff trends to make informed workforce planning decisions. HR professionals lacked comprehensive data analysis tools to understand industry patterns and predict potential workforce risks.",
      objectives: [
        "Analyze global layoff patterns across industries, company sizes, and regions",
        "Identify key factors contributing to layoff decisions",
        "Build predictive models to forecast potential layoff risks",
        "Create automated reporting system for HR professionals"
      ],
      methodology: {
        dataCollection: "Aggregated layoff data from 2000+ companies across 50+ countries covering 2022-2024 period, sourcing from public databases, news reports, and company filings.",
        dataProcessing: "Cleaned and standardized company data, categorized industries, normalized location data, and created time-series datasets using R and advanced data processing techniques.",
        analysis: "Applied statistical analysis including trend analysis, correlation studies, and machine learning algorithms (Random Forest, Logistic Regression) to identify patterns and build predictive models.",
        visualization: "Developed comprehensive Power BI dashboards with interactive filters for industry, region, company size, and time period analysis."
      },
      keyFindings: [
        {
          title: "Industry Impact Patterns",
          description: "Technology sector accounted for 45% of all layoffs despite representing only 15% of workforce, with startups showing 3x higher layoff rates than established companies.",
          impact: "Tech sector layoff rate was 300% higher than traditional industries"
        },
        {
          title: "Geographic Trends",
          description: "North America and Europe showed seasonal layoff patterns with Q4 peaks, while Asia-Pacific demonstrated more event-driven layoffs correlated with economic indicators.",
          impact: "Q4 layoffs were 40% higher than Q2 across developed markets"
        },
        {
          title: "Company Size Correlation",
          description: "Mid-size companies (100-1000 employees) showed highest layoff volatility, while large enterprises demonstrated more predictable patterns.",
          impact: "Mid-size companies had 60% more layoff volatility"
        }
      ],
      technicalChallenges: [
        {
          challenge: "Data Quality and Consistency",
          solution: "Implemented automated data validation pipeline with fuzzy matching algorithms to standardize company names and locations across different data sources."
        },
        {
          challenge: "Missing Data Handling",
          solution: "Developed imputation strategies using industry benchmarks and company characteristics to estimate missing values for 20% of incomplete records."
        },
        {
          challenge: "Model Performance Optimization",
          solution: "Used ensemble methods combining Random Forest and Gradient Boosting to achieve 85% accuracy in layoff prediction models."
        }
      ],
      recommendations: [
        "Implement early warning systems based on economic indicators and industry trends",
        "Develop contingency workforce planning for high-risk industries and periods",
        "Create industry-specific layoff prediction models for improved accuracy",
        "Establish cross-industry talent sharing programs during economic downturns"
      ],
      results: [
        "Built predictive models achieving 85% accuracy in forecasting layoff risks",
        "Created automated reporting system serving 100+ HR professionals",
        "Identified seasonal and industry-specific layoff patterns",
        "Developed early warning framework for workforce planning"
      ],
      nextSteps: [
        "Integrate real-time economic indicators for dynamic model updates",
        "Expand analysis to include skills-based layoff patterns",
        "Develop mobile app for HR professionals to access insights",
        "Create industry-specific prediction models for higher accuracy"
      ],
      gallery: [
        "/api/placeholder/600/400",
        "/api/placeholder/600/400",
        "/api/placeholder/600/400",
        "/api/placeholder/600/400"
      ]
    },
    {
      id: 4,
      title: "Global Income Inequality Analysis",
      slug: "income-inequality",
      description: "Comprehensive analysis of global income inequality trends using WIID data with advanced cleaning methods and interactive dashboards",
      longDescription: "As part of the Infosys Springboard Internship 6.0, I worked in a collaborative team project analyzing the World Income Inequality Database (WIID). I individually applied multiple imputation methods to handle 20%+ missing values, detected and resolved outliers, and improved dataset quality, then designed interactive Power BI dashboards integrated into a Streamlit app.",
      category: "analysis",
      tools: ["Python", "Pandas", "Scikit-learn", "Power BI", "Streamlit"],
      duration: "8 weeks",
      status: "In Progress",
      type: "Data Analysis", 
      highlights: [
        "Cleaned and transformed WIID dataset (~25,000+ records across 180+ countries)",
        "Applied advanced imputation methods (Median, Mode, Interpolation, KNN)",
        "Designed interactive Power BI dashboards on inequality metrics",
        "Integrated dashboards into Streamlit for stakeholder accessibility"
      ],
      githubUrl: "https://github.com/jatinmukati/global-income-inequality",
      liveUrl: "https://inequality-dashboard.streamlit.app",
      image: "/api/placeholder/800/400",
      heroImage: "/api/placeholder/1200/600",
      impact: "Improved data quality by 35% through advanced cleaning techniques",
      problemStatement: "Income inequality is a critical global issue affecting economic stability and social development. The World Income Inequality Database (WIID) contains valuable data but suffers from significant data quality issues including missing values, outliers, and inconsistent reporting standards. This project aimed to clean and analyze this data to provide actionable insights for policymakers.",
      objectives: [
        "Clean and standardize the WIID dataset with advanced imputation techniques",
        "Analyze global income inequality trends across countries and time periods",
        "Identify factors contributing to income inequality variations",
        "Create accessible dashboard for policymakers and researchers"
      ],
      methodology: {
        dataCollection: "Obtained World Income Inequality Database (WIID) containing 25,000+ records across 180+ countries with inequality measures including Gini coefficient, Palma ratio, and quintile data.",
        dataProcessing: "Applied multiple imputation methods (Median, Mode, Linear Interpolation, KNN) to handle 20%+ missing values, implemented outlier detection using IQR and Z-score methods, and standardized country and time period formatting.",
        analysis: "Conducted trend analysis of inequality measures over time, performed correlation analysis between inequality and economic indicators, and applied clustering analysis to group countries by inequality patterns.",
        visualization: "Built interactive Power BI dashboards with geographic heat maps, time series charts, and correlation matrices, then integrated into Streamlit web application for enhanced accessibility."
      },
      keyFindings: [
        {
          title: "Regional Inequality Patterns",
          description: "Latin America consistently shows highest income inequality (Gini >0.5) while Nordic countries maintain lowest inequality levels (Gini <0.3), with trends remaining stable over 20+ years.",
          impact: "Regional differences in Gini coefficient range from 0.25 to 0.65"
        },
        {
          title: "Economic Development Correlation", 
          description: "Middle-income countries show highest inequality volatility while high-income countries demonstrate more stable, lower inequality measures over time.",
          impact: "Middle-income countries show 40% higher inequality volatility"
        },
        {
          title: "Data Quality Improvements",
          description: "Advanced imputation techniques improved dataset completeness from 78% to 95%, with KNN imputation showing best performance for country-specific patterns.",
          impact: "Dataset usability improved by 35% through quality enhancements"
        }
      ],
      technicalChallenges: [
        {
          challenge: "High Missing Data Volume",
          solution: "Implemented multiple imputation strategy comparing Median, Mode, Linear Interpolation, and KNN methods, selecting optimal approach based on data distribution and country characteristics."
        },
        {
          challenge: "Outlier Detection and Treatment", 
          solution: "Developed hybrid outlier detection using both statistical (IQR, Z-score) and domain-knowledge approaches to preserve legitimate extreme values while removing errors."
        },
        {
          challenge: "Dashboard Performance with Large Dataset",
          solution: "Implemented data aggregation strategies and optimized Power BI queries to ensure interactive dashboard performance with 25K+ records."
        }
      ],
      recommendations: [
        "Establish standardized inequality reporting frameworks for consistent global monitoring",
        "Implement targeted policies in high-inequality regions based on successful Nordic models",
        "Develop early warning systems for inequality trend monitoring",
        "Create public data repository with cleaned, standardized inequality metrics"
      ],
      results: [
        "Improved WIID dataset quality from 78% to 95% completeness",
        "Created comprehensive inequality dashboard serving researchers and policymakers",
        "Identified key regional and temporal inequality patterns",
        "Developed reusable data cleaning pipeline for similar datasets"
      ],
      nextSteps: [
        "Expand analysis to include sub-national inequality data",
        "Integrate machine learning models for inequality prediction",
        "Develop mobile-responsive dashboard version",
        "Create automated data update pipeline for ongoing monitoring"
      ],
      gallery: [
        "/api/placeholder/600/400",
        "/api/placeholder/600/400", 
        "/api/placeholder/600/400",
        "/api/placeholder/600/400"
      ]
    }
  ],

  experience: [
    {
      title: "Freelance Data Analyst",
      company: "Self-Employed",
      duration: "Jan 2024 - Present",
      location: "Mumbai, India",
      responsibilities: [
        "Conducted comprehensive data analysis for 5+ small businesses using SQL and Python",
        "Created interactive dashboards in Tableau resulting in 25% improvement in decision-making speed",
        "Performed statistical analysis and A/B testing to optimize marketing campaigns",
        "Collaborated with cross-functional teams to identify key business metrics and KPIs"
      ]
    },
    {
      title: "Data Analytics Intern",
      company: "TechStart Solutions",
      duration: "Jun 2023 - Dec 2023",
      location: "Mumbai, India",
      responsibilities: [
        "Analyzed customer behavior data using Python and SQL to identify trends and patterns",
        "Developed automated reporting systems reducing manual work by 40%",
        "Assisted in building predictive models for customer churn analysis",
        "Presented findings to senior management and stakeholders"
      ]
    }
  ],

  education: [
    {
      degree: "Bachelor of Technology in Computer Science",
      institution: "Mumbai University",
      duration: "2020 - 2024",
      gpa: "8.5/10",
      relevant: ["Statistics", "Database Management", "Data Structures", "Machine Learning"]
    }
  ],

  certifications: [
    {
      name: "Google Data Analytics Professional Certificate",
      issuer: "Google",
      date: "2024",
      status: "Completed",
      credentialId: "ABC123456"
    },
    {
      name: "SQL for Data Science",
      issuer: "Coursera",
      date: "2024",
      status: "Completed",
      credentialId: "DEF789012"
    },
    {
      name: "Python for Data Analysis",
      issuer: "DataCamp",
      date: "2023",
      status: "Completed",
      credentialId: "GHI345678"
    },
    {
      name: "Tableau Desktop Specialist",
      issuer: "Tableau",
      date: "2023",
      status: "In Progress"
    }
  ],

  testimonials: [
    {
      name: "Sarah Johnson",
      position: "Marketing Director",
      company: "TechStart Solutions",
      testimonial: "Jatin's analytical skills and attention to detail helped us optimize our marketing campaigns and increase ROI by 30%.",
      rating: 5
    },
    {
      name: "Raj Patel",
      position: "Business Owner",
      company: "Local Restaurant Chain",
      testimonial: "The insights Jatin provided through data analysis helped us understand our customer behavior and improve our service delivery.",
      rating: 5
    }
  ],

  stats: {
    yearsExperience: "3+",
    projectsCompleted: "15+",
    happyClients: "5+",
    certifications: "4"
  }
};

// Contact form submission (mock)
export const submitContactForm = async (formData) => {
  // Simulate API call delay
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log("Form submitted with data:", formData);
      resolve({ success: true, message: "Message sent successfully!" });
    }, 1500);
  });
};

// Resume download (mock)
export const downloadResume = () => {
  // Mock resume download
  console.log("Resume download initiated");
  alert("Resume download will be implemented with backend integration");
};

export default portfolioData;